import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  getDisputeById, 
  addDisputeMessage, 
  resolveDispute, 
  checkAdminStatus 
} from '../../services/disputeService';
import { 
  Dispute, 
  DisputeMessage, 
  DisputeStatus, 
  DisputeResolution, 
  MessageVisibility 
} from '../../types/dispute';
import ResolveDisputeForm from '../ResolveDisputeForm/ResolveDisputeForm';
import './DisputeDetail.css';
import LabeledValue from '../LabeledValue';

const DisputeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [dispute, setDispute] = useState<Dispute | null>(null);
  const [messages, setMessages] = useState<DisputeMessage[]>([]);
  const [newMessage, setNewMessage] = useState<string>('');
  const [messageVisibility, setMessageVisibility] = useState<MessageVisibility>(MessageVisibility.BothParties);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Check if user is admin
        const adminStatus = await checkAdminStatus();
        setIsAdmin(adminStatus);

        console.log("Admin status", adminStatus);

        if (!id) return;
        
        // Fetch dispute details
        const data = await getDisputeById(id) as any;
        setDispute(data.dispute);
        setMessages(data.messages);
      } catch (err: any) {
        setError(err.message || 'Failed to load dispute details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!id || !newMessage.trim()) {
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const messageData = {
        message: newMessage.trim(),
        visibility: isAdmin ? messageVisibility : undefined
      };
      
      const response = await addDisputeMessage(id, messageData) as any;
      setMessages([...messages, response]);
      setNewMessage('');
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleResolveDispute = async (resolution: string, notes: string) => {
    if (!id) return;
    
    setError(null);
    
    try {
      const resolutionData = {
        resolution,
        notes: notes.trim() || undefined
      };
      
      const updatedDispute = await resolveDispute(id, resolutionData) as any;
      setDispute(updatedDispute);
      
      // Refresh messages
      const data = await getDisputeById(id) as any;
      setMessages(data.messages);
      
      return Promise.resolve();
    } catch (err: any) {
      setError(err.message || 'Failed to resolve dispute');
      return Promise.reject(err);
    }
  };
  
  if (loading) {
    return <div className="dispute-detail-loading">Loading dispute details...</div>;
  }
  
  if (!dispute) {
    return (
      <div className="dispute-detail-error-container">
        <h2>Dispute Not Found</h2>
        <p>The dispute you're looking for doesn't exist or you don't have permission to view it.</p>
        {error && <p className="dispute-detail-error-message">{error}</p>}
      </div>
    );
  }
  
  return (
    <div className="dispute-detail-container">
      <div className="escrow-card-section">
        <div className="dispute-detail-header">
          <h1>Dispute Details</h1>
        </div>
        <div className="dispute-detail-status">
            Status: <span className={`dispute-detail-status-badge dispute-detail-status-${dispute.status}`}>{dispute.status}</span>
          </div>

        <div className="dispute-detail-info">
          <LabeledValue label="Transaction ID" value={dispute.transactionId} />
        </div>
        
        <div className="dispute-detail-info">
          <LabeledValue label="Reason" value={dispute.reason} />
          <LabeledValue label="Created At" value={new Date(dispute.createdAt).toLocaleString()} />
          
          {dispute.status === DisputeStatus.Resolved && (
            <div className="dispute-detail-resolution-info">
              <h3>Resolution</h3>
              <p>
                <LabeledValue label="Resolution" value={
                  dispute.resolution === DisputeResolution.RefundToBuyer
                    ? 'Refund to Buyer'
                    : 'Release to Seller'
                }  />
              </p>
              {dispute.resolutionNotes && (
                <p><LabeledValue label="Notes" value={dispute.resolutionNotes} /></p>
              )}
              <p><LabeledValue label="Resolved on" value={new Date(dispute.updatedAt).toLocaleString()} /></p>
            </div>
          )}
        </div>
      </div>
      
      <div className="dispute-detail-message-container escrow-card-section">
        <h2>Messages</h2>
        
        {messages.length === 0 ? (
          <p>No messages yet.</p>
        ) : (
          <div className="dispute-detail-message-list">
            {messages.map(message => (
              <div key={message.id} className={`dispute-detail-message dispute-detail-message-${message.senderRole.toLowerCase()}`}>
                <div className="dispute-detail-message-header">
                  <span className="dispute-detail-sender">{message.senderRole}</span>
                  <span className="dispute-detail-date">{new Date(message.createdAt).toLocaleString()}</span>
                </div>
                <div className="dispute-detail-message-content">
                  {message.message}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {dispute.status !== DisputeStatus.Resolved && (
          <div className="dispute-detail-new-message-form">
            {error && (
              <div className="dispute-detail-alert dispute-detail-alert-danger">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSendMessage}>
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message here..."
                required
                maxLength={2000}
                rows={4}
              />
              
              {isAdmin && (
                <div className="dispute-detail-visibility-controls">
                  <label>Visible to:</label>
                  <select
                    value={messageVisibility}
                    onChange={(e) => setMessageVisibility(e.target.value as MessageVisibility)}
                  >
                    <option value={MessageVisibility.AdminOnly}>Admin Only</option>
                    <option value={MessageVisibility.BuyerOnly}>Buyer Only</option>
                    <option value={MessageVisibility.SellerOnly}>Seller Only</option>
                    <option value={MessageVisibility.BothParties}>Both Parties</option>
                  </select>
                </div>
              )}
              
              <button type="submit" className="submit-button" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        )}
      </div>
      
      {isAdmin && dispute.status !== DisputeStatus.Resolved && (
        <div className="dispute-detail-admin-resolution-controls">
          <h3>Resolve Dispute</h3>
          <ResolveDisputeForm onResolve={handleResolveDispute} />
        </div>
      )}
    </div>
  );
};

export default DisputeDetail;